import { useState, useEffect } from 'react';
import { Trash2, Mail, MailOpen, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { adminApi } from '@/lib/api';
import { toast } from 'sonner';

const ContactsManager = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedContact, setSelectedContact] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      const response = await adminApi.getContacts();
      setContacts(response.data);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      toast.error('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  };

  const handleView = async (contact) => {
    setSelectedContact(contact);
    setDialogOpen(true);
    if (!contact.read) {
      try {
        await adminApi.markContactRead(contact.id);
        fetchContacts();
      } catch (error) {
        console.error('Error marking contact read:', error);
      }
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this message?')) return;
    try {
      await adminApi.deleteContact(id);
      toast.success('Contact deleted');
      fetchContacts();
    } catch (error) {
      console.error('Error deleting contact:', error);
      toast.error('Failed to delete contact');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div data-testid="contacts-manager">
      <div className="mb-8">
        <h1 className="text-3xl font-heading font-bold mb-2">Contact Messages</h1>
        <p className="text-muted-foreground">View and manage form submissions.</p>
      </div>

      <div className="admin-card">
        <table className="admin-table">
          <thead>
            <tr>
              <th></th>
              <th>Name</th>
              <th>Email</th>
              <th>Subject</th>
              <th>Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {contacts.map((contact) => (
              <tr
                key={contact.id}
                className={!contact.read ? 'bg-primary/5' : ''}
                data-testid={`contact-row-${contact.id}`}
              >
                <td>
                  {contact.read ? (
                    <MailOpen className="w-4 h-4 text-muted-foreground" />
                  ) : (
                    <Mail className="w-4 h-4 text-primary" />
                  )}
                </td>
                <td className={`font-medium ${!contact.read ? 'text-foreground' : 'text-muted-foreground'}`}>
                  {contact.name}
                </td>
                <td className="text-muted-foreground">{contact.email}</td>
                <td className="text-muted-foreground truncate max-w-[200px]">{contact.subject}</td>
                <td className="text-muted-foreground text-sm">{formatDate(contact.created_at)}</td>
                <td>
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleView(contact)}
                      data-testid={`view-contact-${contact.id}`}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(contact.id)}
                      className="text-red-400 hover:text-red-300"
                      data-testid={`delete-contact-${contact.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
            {contacts.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center text-muted-foreground py-8">
                  No contact messages yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0f172a] border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Contact Message</DialogTitle>
          </DialogHeader>
          {selectedContact && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Name</p>
                  <p className="font-medium">{selectedContact.name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Email</p>
                  <a href={`mailto:${selectedContact.email}`} className="text-primary hover:underline">
                    {selectedContact.email}
                  </a>
                </div>
              </div>
              {selectedContact.phone && (
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Phone</p>
                  <p>{selectedContact.phone}</p>
                </div>
              )}
              <div>
                <p className="text-sm text-muted-foreground mb-1">Subject</p>
                <p className="font-medium">{selectedContact.subject}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Message</p>
                <p className="text-muted-foreground whitespace-pre-wrap">{selectedContact.message}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Received</p>
                <p className="text-sm">{formatDate(selectedContact.created_at)}</p>
              </div>
              <div className="flex gap-2 justify-end pt-4">
                <a href={`mailto:${selectedContact.email}?subject=Re: ${selectedContact.subject}`}>
                  <Button className="btn-primary" data-testid="reply-contact-btn">
                    <Mail className="w-4 h-4 mr-2" />
                    Reply
                  </Button>
                </a>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContactsManager;
